import {Component, computed, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {
  testingResultFilesResourceType,
  testingResultsFileType,
  testingResultsResourceType
} from "./configs/testing-result-files-config";
import {TestingResultFileData, TestingResultFilesDialogData} from "./types/testing-result-files.types";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {TestingResultFilesService} from "./services/testing-result-files.service";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {RecordData, RecordUpdateRequestParams} from "../../../../../../core/types/records-table.types";
import {FileCardComponent} from "../../../../../../shared/components/ui/file-card/file-card.component";
import {
  FileUploadCardComponent
} from "../../../../../../shared/components/ui/file-upload-card/file-upload-card.component";
import {AttachProductCardFileParams, DeleteFileParams} from "../../../types/product-card-files.types";
import {AttachFileParams} from "../../../../../../core/types/file.types";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-testing-result-files-modal',
  imports: [
    BaseModalComponent,
    CdkTextareaAutosize,
    MatInput,
    ReactiveFormsModule,
    FormsModule,
    MatButton,
    MatFormField,
    MatLabel,
    FileCardComponent,
    FileUploadCardComponent,
    LoaderComponent
  ],
  templateUrl: './testing-result-files-modal.component.html',
  styleUrl: './testing-result-files-modal.component.scss'
})
export class TestingResultFilesModalComponent extends BaseModalComponent {
  public testingResultFilesDialogData = inject<TestingResultFilesDialogData>(MAT_DIALOG_DATA);
  private testingResultFilesService: TestingResultFilesService = inject(TestingResultFilesService);

  public readonly testingResultFilesResourceType = testingResultFilesResourceType;
  public readonly testingResultsFileType = testingResultsFileType;
  public testingResultFilesData = signal<Array<TestingResultFileData>>([]);
  public testingResultId = computed(() => Number(this.testingResultFilesDialogData.parentData.id));

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(testingResultFilesResourceType.path);
  }

  public async handleUpdateComment(): Promise<void> {
    const recordUpdateRequestParams: RecordUpdateRequestParams = {
      path: testingResultsResourceType.path,
      recordId: this.testingResultId(),
      fieldName: 'comment',
      value: this.testingResultFilesDialogData.comment
    };

    await this.updatedRecordField<RecordData>(recordUpdateRequestParams);
  }

  public async handleDelete(isImage: boolean, id: number): Promise<void> {
    const params: DeleteFileParams = {
      id: id,
      path: testingResultFilesResourceType.path
    };

    this.testingResultFilesService.deleteFile(params)
      .then(() => this.testingResultFilesData.update((filesData) =>
        filesData.filter((data) => data.id !== id)
      ));
  }

  public async attachFile({ parentId, fileId }: AttachFileParams): Promise<void> {
    const attachFileParams: AttachProductCardFileParams = {
      path: testingResultFilesResourceType.path,
      body: {
        fileId: fileId,
        testingResultId: parentId
      }
    };

    await this.testingResultFilesService.attachFile<TestingResultFileData>(attachFileParams)
      .then((data) => this.testingResultFilesData.update(currentFiles => [ ...currentFiles, data ]));
  }

  private async initModal(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    this.modalStatesService.openModal(testingResultFilesResourceType.path);
    await this.fetchFiles();
  }

  private async fetchFiles(): Promise<void> {
    const params = this.testingResultFilesService.setDataInModalParams(this.testingResultId());
    await this.testingResultFilesService.getDataInModal<TestingResultFileData>(params)
      .then((data) => this.testingResultFilesData.set(data))
      .catch(() => this.testingResultFilesData.set([]))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }
}
